import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, analytics } from '../../../firebase';
import { signOut } from 'firebase/auth';
import { getDoc } from 'firebase/firestore';
// logEvent also feeds the first-party stream via the GA4 bridge;
// the signature and GA4 behaviour are unchanged.
import { logEvent } from '../../../analytics/ga4';
import { sendLoginSummaryToSupport } from './functions/TriggerLoginSummaryEmail';
import InfoTooltip from '../../../components/InfoTooltip';
import OtpInput from '../../../components/auth/OtpInput';
import TotpEnrollmentPanel from '../../../components/auth/TotpEnrollmentPanel';
import MfaMethodSelector from '../../../components/auth/MfaMethodSelector';
import { getBackgroundGradient } from '../../../utils/environmentTheme';
import { getInformantDashboardPath, getPatientDashboardPath } from '../../../utils/informantDashboardRouting';
import { getServiceUserProfileDocRef } from '../../../utils/informantFirestorePaths';
import useMfaEnrollment from '../../../hooks/auth/useMfaEnrollment';

// PatientSetUpMFA
// ---------------------------------------------------------------------------
// Design notes:
// - Optional MFA for service users, in three focused steps so the card stays a
//   moderate height: (1) intro — explain optional 2FA + "Set up now" / "Skip",
//   (2) method pick — Authenticator (Recommended) vs SMS, (3) enroll that method.
// - "Skip and set up later" and sign-out stay available on every step (2FA is
//   optional for service users, unlike providers).
// - Both SMS and TOTP are offered in ALL environments for service users.
// - All enrollment mechanics (reCAPTCHA, SMS send/verify, TOTP, method switch,
//   the shared-test-number dev bypass, Firestore writes) live in the shared
//   `useMfaEnrollment` hook so this page and the provider page stay in parity.
//   This page only owns the service-user-specific bits: the optional intro/skip,
//   sign-out routing, post-enrollment navigation, and which Firestore doc to
//   write to.
export default function PatientSetUpMFA() {
  const navigate = useNavigate();

  // Optional-setup intro state (service-user only): the user must opt in to
  // setup via "Set up 2FA now" before the method picker appears.
  const [wantsSetup, setWantsSetup] = useState(false);

  // Analytics identity + claims used to resolve the correct profile document.
  const accountType = 'Service User';
  const [userType, setUserType] = useState('');
  const userClaimsRef = useRef(null);

  // Fetch userType for analytics (best-effort) and store claims for the profile
  // resolver (patients vs informants collection).
  useEffect(() => {
    const u = auth?.currentUser;
    if (!u) return;
    try {
      u.getIdTokenResult(true)
        .then((res) => {
          try {
            if (res?.claims?.userType) setUserType(res.claims.userType);
            userClaimsRef.current = res?.claims || null;
          } catch (_) {}
        })
        .catch(() => {});
    } catch (_) {}
  }, []);

  // Where to persist MFA status: the service user's own profile document.
  const resolveDocRef = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return null;
    const ref = getServiceUserProfileDocRef(user.uid, userClaimsRef.current);
    const snap = await getDoc(ref);
    return { ref, snap };
  }, []);

  // After any successful enrollment: first-time users see the welcome screen,
  // returning users go to their role-appropriate dashboard.
  const onEnrolled = useCallback(async () => {
    const user = auth.currentUser;
    const userDoc = await getDoc(getServiceUserProfileDocRef(user.uid, userClaimsRef.current));
    const userData = userDoc.data();
    if (!userData?.hasSeenWelcome) {
      navigate('/service-user/welcome');
      return;
    }
    const userClaims = await auth.currentUser.getIdTokenResult();
    if (userClaims.claims.userType === 'Informant') {
      navigate(getInformantDashboardPath(userClaims.claims));
    } else {
      sendLoginSummaryToSupport(user); // Send update to support team
      navigate(getPatientDashboardPath(userClaims.claims));
    }
  }, [navigate]);

  // Shared enrollment engine. Service users get both methods in every
  // environment; nothing is pre-selected (the picker is shown after opt-in).
  const enrollment = useMfaEnrollment({
    allowSms: true,
    initialMethod: null,
    resolveDocRef,
    onEnrolled,
    analytics: { accountType, userType, eventPrefix: 'patient_', logFailures: false },
  });

  const {
    selectedMethod,
    handleSelectMethod,
    handleChangeMethod,
    isMethodLocked,
    error,
    errorCode,
    verificationCode,
    setVerificationCode,
    panelContentRef,
    panelHeight,
    cleanupRecaptcha,
    phoneNumber,
    isPhoneValid,
    handlePhoneChange,
    isCodeSent,
    isSending,
    handleSendCode,
    handleResendCode,
    handleVerifySms,
    resendSecondsLeft,
    isResending,
    resendJustSent,
    last4,
    totpUri,
    totpSecretKey,
    isTotpReady,
    isTotpError,
    isTotpEnrolling,
    copiedSecret,
    handleVerifyTotpCode,
    handleCopySecret,
    handleRetryTotp,
  } = enrollment;

  // Skip MFA (service users only): record the choice, then route onward.
  const handleSkip = async () => {
    logEvent(analytics, 'patient_mfa_setup_skipped');
    logEvent(analytics, 'login', { method: 'email' });

    const user = auth.currentUser;
    const userDoc = await getDoc(getServiceUserProfileDocRef(user.uid, userClaimsRef.current));
    const userData = userDoc.data();
    if (!userData?.hasSeenWelcome) {
      navigate('/service-user/welcome');
      return;
    }
    const idTokenResult = await user.getIdTokenResult(true);
    if (idTokenResult.claims.userType === 'Informant') {
      navigate(getInformantDashboardPath(idTokenResult.claims));
    } else {
      sendLoginSummaryToSupport(user); // Send update to support team
      navigate(getPatientDashboardPath(idTokenResult.claims));
    }
  };

  const handleSignOut = async () => {
    try {
      cleanupRecaptcha(); // tear down reCAPTCHA before leaving
      await signOut(auth);
      navigate('/service-user/login');
    } catch (err) {
      console.error('Error signing out:', err);
      navigate('/service-user/login'); // still attempt to leave
    }
  };

  // Ensure consistent vertical spacing at the top of the panel: in dev/staging
  // we render an environment banner that provides top space; in production (no
  // banner), we add top margin to the logo so it doesn't touch the card edge.
  const showEnvBanner =
    import.meta.env.VITE_ENVIRONMENT === 'development' || import.meta.env.VITE_ENVIRONMENT === 'staging';

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundImage: getBackgroundGradient() }}>
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl ring-1 ring-black/5 px-8 pb-8 mx-4">
        <div className="w-full">
          {showEnvBanner && (
            <div
              className={`-mx-8 mb-10 px-8 py-3 border font-semibold text-center rounded-t-2xl
              ${import.meta.env.VITE_ENVIRONMENT === 'development' ? 'bg-yellow-200 text-yellow-900 border-yellow-300' : 'bg-blue-200 text-blue-900 border-blue-300'}
            `}
            >
              <div className="flex items-center justify-center gap-2">
                <span>
                  {import.meta.env.VITE_ENVIRONMENT === 'development' ? 'Development Environment' : 'Testing / Training Environment'}
                </span>
              </div>
            </div>
          )}
          <img
            src="/td_logo_full_2025.png"
            alt="Logo"
            className={`mx-auto mb-8 ${!showEnvBanner ? 'mt-10' : ''}`}
            style={{ width: '220px', height: 'auto' }}
          />
          <h2 className="text-xl font-bold mb-2 text-center">
            Two‑Factor Authentication{' '}
            <span className="text-base font-semibold text-gray-500">(Optional)</span>
          </h2>

          {/* ----------------------------------------------------------------- */}
          {/* Step 1 — Intro: explain optional 2FA in plain language.           */}
          {/* ----------------------------------------------------------------- */}
          {!wantsSetup && (
            <div className="text-gray-600 text-sm mb-6 text-center space-y-3">
              <p>This is an optional, extra security step.</p>
              <p>If you choose to set it up, you will be asked for a 6-digit code each time you sign in.</p>
              <p>If you choose to &apos;Skip and set up later&apos;, we will remind you when you next sign in.</p>
            </div>
          )}

          {/* ----------------------------------------------------------------- */}
          {/* Step 2 — Method pick: Authenticator (Recommended) vs SMS.         */}
          {/* Shown after "Set up 2FA now"; skip/sign-out stay below.           */}
          {/* ----------------------------------------------------------------- */}
          {wantsSetup && !selectedMethod && (
            <>
              <div className="text-gray-600 text-sm mb-4 text-center space-y-2">
                <p>Choose how you would like to receive your sign-in codes below.</p>
                <p>
                  An authenticator app is the most reliable option - codes work even when your phone has no
                  signal or text messages are delayed. Text messages (SMS) are still available if you prefer.
                </p>
              </div>
              <MfaMethodSelector selectedMethod={null} onSelect={handleSelectMethod} disabled={false} />
            </>
          )}

          {/* ----------------------------------------------------------------- */}
          {/* Step 3 — Focused enrollment for the chosen method.                */}
          {/* "Change method" returns to step 2 (not the intro).                */}
          {/* ----------------------------------------------------------------- */}
          {selectedMethod && (
            <>
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-semibold text-gray-900">
                  {selectedMethod === 'totp' ? 'Authenticator App' : 'SMS Text'}
                </span>
                {!isMethodLocked && (
                  <button
                    type="button"
                    onClick={handleChangeMethod}
                    className="text-xs font-medium text-primary hover:text-primary/80 underline"
                  >
                    Change method
                  </button>
                )}
              </div>

              <div
                className="overflow-hidden transition-[height,opacity] duration-300 ease-out"
                style={{ height: panelHeight || 'auto', opacity: 1 }}
              >
                <div ref={panelContentRef}>
                  {/* ----- SMS MFA Flow ----- */}
                  {selectedMethod === 'sms' && (
                    <div className="animate-fadeIn">
                      <div className="text-gray-600 text-sm mb-4 text-center">
                        {!isCodeSent ? (
                          <p>Enter your mobile number to receive a verification code via SMS.</p>
                        ) : (
                          <p>{`A 6-digit code has been sent to your mobile phone${last4 ? ` ending in ••••${last4}` : ''}. Enter it below.`}</p>
                        )}
                      </div>

                      {!isCodeSent ? (
                        <div>
                          <div className="mb-4">
                            <div className="flex items-center justify-between mb-1">
                              <label className="block text-xs font-bold text-gray-700 text-left">Mobile Phone Number</label>
                              <InfoTooltip side="right" content={'We currently only support UK phone numbers.'} />
                            </div>
                            <div className="flex items-center gap-2 w-full">
                              <div className="shrink-0 inline-flex items-center gap-2 px-3 py-2 border rounded-md bg-white text-gray-800">
                                <img src="/gb_flag.svg" alt="GB" className="h-4 w-auto" />
                                <span className="font-semibold">+44</span>
                              </div>
                              <input
                                type="tel"
                                value={phoneNumber}
                                inputMode="tel"
                                pattern="[0-9 ]*"
                                onChange={(e) => handlePhoneChange(e.target.value)}
                                placeholder="e.g. 07123456789"
                                className="flex-1 min-w-0 px-4 py-2 mr-1 border rounded-md focus:outline-none focus:ring focus:ring-primary placeholder:text-sm"
                              />
                            </div>
                            <div className={`mt-2 overflow-hidden transition-all duration-300 ${phoneNumber ? 'max-h-6' : 'max-h-0'}`}>
                              <div className={`text-sm transition-opacity duration-300 ${phoneNumber ? 'opacity-100' : 'opacity-0'} ${isPhoneValid ? 'text-green-600' : 'text-gray-500'}`}>
                                {isPhoneValid ? '✔ Phone number valid' : '✘ Phone number not valid'}
                              </div>
                            </div>
                          </div>
                          {/* Primary: Send code */}
                          <button
                            id="send-code-button"
                            onClick={handleSendCode}
                            disabled={!isPhoneValid || isSending}
                            className={`w-full font-semibold py-2 rounded-lg transition duration-100 mb-3 ${
                              !isPhoneValid || isSending ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-primary text-white hover:bg-primary/90'
                            }`}
                          >
                            {isSending ? (
                              <div className="flex items-center justify-center">
                                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                Sending...
                              </div>
                            ) : (
                              'Send Verification Code'
                            )}
                          </button>
                        </div>
                      ) : (
                        <div>
                          <div className="mb-4">
                            <OtpInput label="Verification Code" value={verificationCode} onChange={setVerificationCode} autoFocus />
                          </div>
                          <button
                            onClick={handleVerifySms}
                            disabled={(verificationCode || '').length !== 6}
                            className={`w-full font-semibold py-2 rounded-lg transition duration-100 mb-3 ${
                              (verificationCode || '').length !== 6 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-primary text-white hover:bg-primary/90'
                            }`}
                          >
                            Verify Code
                          </button>
                          <button
                            type="button"
                            onClick={handleResendCode}
                            disabled={resendSecondsLeft > 0 || isResending}
                            id="resend-code-button"
                            className={`w-full font-semibold bg-white text-primary py-2 rounded-lg border border-primary hover:bg-primary/5 transition duration-100 disabled:text-gray-400 disabled:border-gray-300 disabled:bg-transparent mb-3 relative`}
                          >
                            <span className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${resendJustSent ? 'opacity-100' : 'opacity-0'}`}>
                              ✔ Code sent
                            </span>
                            <span className={`transition-opacity duration-300 ${resendJustSent ? 'opacity-0' : 'opacity-100'}`}>
                              {`Resend Code${resendSecondsLeft > 0 ? ` (${resendSecondsLeft}s)` : ''}`}
                            </span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ----- TOTP / Authenticator App MFA Flow ----- */}
                  {selectedMethod === 'totp' && (
                    <div className="animate-fadeIn">
                      {/* Shared authenticator-app enrollment UI (QR + manual key + OTP).
                          The retry prompt below owns the generation-failure case, so we
                          only render the panel while there is no generation error. */}
                      {!isTotpError && (
                        <TotpEnrollmentPanel
                          isReady={isTotpReady}
                          totpUri={totpUri}
                          totpSecretKey={totpSecretKey}
                          code={verificationCode}
                          onCodeChange={setVerificationCode}
                          otpDisabled={isTotpEnrolling}
                          copied={copiedSecret}
                          onCopySecret={handleCopySecret}
                          qrSize={180}
                          introText="Scan the QR code below with your authenticator app, then enter the 6-digit code it generates."
                          showAppGuidance
                        >
                          <button
                            onClick={handleVerifyTotpCode}
                            disabled={(verificationCode || '').length !== 6 || isTotpEnrolling}
                            className={`w-full font-semibold py-2 rounded-lg transition duration-100 ${(verificationCode || '').length !== 6 || isTotpEnrolling ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-primary text-white hover:bg-primary/90'}`}
                          >
                            {isTotpEnrolling ? (
                              <div className="flex items-center justify-center">
                                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                Verifying...
                              </div>
                            ) : (
                              'Verify & Complete Setup'
                            )}
                          </button>
                        </TotpEnrollmentPanel>
                      )}

                      {/* Error/retry state when TOTP secret generation failed. */}
                      {!isTotpReady && isTotpError && (
                        <div className="text-center py-4">
                          <p className="text-sm text-gray-500 mb-3">Could not generate authenticator setup.</p>
                          <button
                            type="button"
                            onClick={handleRetryTotp}
                            className="text-sm font-medium text-primary hover:text-primary/80 underline"
                          >
                            Try again
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Consolidated error banner (shared by both methods) */}
          <div className={`transition-all duration-300 overflow-hidden ${error ? 'max-h-24 mt-3 mb-0' : 'max-h-0 mb-0'}`} role="alert">
            <div className={`bg-red-100 border border-red-400 text-red-700 px-4 py-3 text-sm rounded-lg relative ${error ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}>
              <span className="block sm:inline">
                {error}
                {errorCode ? ` (Code: ${errorCode})` : ''}
              </span>
            </div>
          </div>

          {/* Intro primary CTA — advance to method selection */}
          {!wantsSetup && (
            <button
              type="button"
              onClick={() => setWantsSetup(true)}
              className="w-full font-semibold bg-primary text-white py-2 rounded-lg hover:bg-primary/90 transition duration-100 mt-1"
            >
              Set up 2FA now
            </button>
          )}

          {/* Skip — always available (2FA is optional for service users) */}
          <button
            type="button"
            onClick={handleSkip}
            className="w-full font-semibold bg-white text-primary py-2 rounded-lg border border-primary hover:bg-primary/5 transition duration-100 mt-3"
          >
            Skip and set up later
          </button>

          <button
            type="button"
            onClick={handleSignOut}
            className="w-full text-gray-500 text-sm font-semibold hover:text-gray-700 transition duration-100 mt-6"
          >
            ... or sign out instead
          </button>
          {/* Support footer for service users */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">Need help? Contact your healthcare provider.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
